import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "services/supabaseClient";

function preferenceId(category, key) {
  return `${category}\u0000${key}`;
}

export default function useUserPreferences(userId) {
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    async function loadPreferences() {
      if (!userId) {
        setPreferences([]);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      const { data, error: loadError } = await supabase
        .from("user_preferences")
        .select("preference_category, preference_key, preference_value")
        .eq("user_id", userId);

      if (!active) return;
      if (loadError) {
        setError(loadError);
        setPreferences([]);
      } else {
        setPreferences(data || []);
      }
      setLoading(false);
    }

    loadPreferences();
    return () => { active = false; };
  }, [userId]);

  const preferenceMap = useMemo(() => new Map(preferences.map((preference) => [
    preferenceId(preference.preference_category, preference.preference_key),
    preference.preference_value,
  ])), [preferences]);

  const getPreference = useCallback(
    (category, key) => preferenceMap.get(preferenceId(category, key)),
    [preferenceMap]
  );

  const setPreference = useCallback(async (category, key, value) => {
    if (!userId) throw new Error("A signed-in user is required to save preferences.");
    const row = {
      user_id: userId,
      preference_category: category,
      preference_key: key,
      preference_value: value,
    };
    const { error: saveError } = await supabase
      .from("user_preferences")
      .upsert(row, { onConflict: "user_id,preference_category,preference_key" });
    if (saveError) {
      setError(saveError);
      throw saveError;
    }
    setError(null);
    setPreferences((current) => [
      ...current.filter((preference) =>
        preference.preference_category !== category || preference.preference_key !== key
      ),
      row,
    ]);
  }, [userId]);

  const removePreference = useCallback(async (category, key) => {
    if (!userId) throw new Error("A signed-in user is required to save preferences.");
    const { error: removeError } = await supabase
      .from("user_preferences")
      .delete()
      .eq("user_id", userId)
      .eq("preference_category", category)
      .eq("preference_key", key);
    if (removeError) {
      setError(removeError);
      throw removeError;
    }
    setError(null);
    setPreferences((current) => current.filter((preference) =>
      preference.preference_category !== category || preference.preference_key !== key
    ));
  }, [userId]);

  return { preferences, loading, error, getPreference, setPreference, removePreference };
}
